import { useState } from 'react';
import { apiClient } from '../lib/api-client';
import { useAuth } from '../hooks/useAuth';
import { ScanForm } from './ScanForm';
import { ScanProgress } from './ScanProgress';
import { TeamScanProgress } from './TeamScanProgress';
import type { ScanRequest, RepoExecution } from '@sdd-maturity/shared-types';

const SCAN_KEY = 'sdd-maturity-active-scan';
const TEAM_SCAN_KEY = 'sdd-maturity-team-scan';
const SCAN_TTL_MS = 15 * 60 * 1000; // 15 min — auto-expire stale scans

interface PersistedTeamScan {
  repoCount: number;
  teamName: string;
  teamNumber: string;
  startTime: number;
  repoExecutions: RepoExecution[];
}

// Storage is keyed per-team so a scan started under one team never bleeds into
// another team's phase page (the panel is single-team, single-phase by design).
function keyFor(base: string, teamNumber: string): string {
  return `${base}:${teamNumber}`;
}

function loadFromStorage<T>(base: string, teamNumber: string): T | null {
  try {
    const raw = localStorage.getItem(keyFor(base, teamNumber));
    if (!raw) return null;
    const envelope = JSON.parse(raw) as { ts: number; data: T };
    if (Date.now() - envelope.ts > SCAN_TTL_MS) {
      localStorage.removeItem(keyFor(base, teamNumber));
      return null;
    }
    return envelope.data;
  } catch {
    return null;
  }
}

function saveToStorage<T>(base: string, teamNumber: string, data: T) {
  try { localStorage.setItem(keyFor(base, teamNumber), JSON.stringify({ ts: Date.now(), data })); } catch { /* noop */ }
}

function clearStorage(base: string, teamNumber: string) {
  try { localStorage.removeItem(keyFor(base, teamNumber)); } catch { /* noop */ }
}

/**
 * Inline Delivery scan for one team, rendered at the bottom of the team's
 * Delivery phase page. Replaces the old `/scan` page + Discovery/Delivery
 * toggle — the phase is implied by which page hosts the panel. On a terminal
 * status the panel calls `onScanComplete` so the page can refetch and show the
 * fresh scores in place. Delivery scan is team-only; ScanForm renders the
 * team-scan trigger (the legacy URL/org ad-hoc scan modes were removed — see
 * ScanForm and git history for restoration).
 */
export function DeliveryScanPanel({
  teamNumber,
  teamName,
  onScanComplete,
  lastScannedAt,
  align,
}: {
  teamNumber: string;
  teamName: string;
  onScanComplete?: () => void;
  /** Most recent scan timestamp across this team's repos; shows "Last scanned X ago". */
  lastScannedAt?: string | null;
  /** Alignment of the scan line; 'right' tucks it under the table's bottom-right. */
  align?: 'left' | 'right';
}) {
  const { token } = useAuth();
  const [executionArn, setExecutionArn] = useState<string | null>(() => loadFromStorage<string>(SCAN_KEY, teamNumber));
  const [submitting, setSubmitting] = useState(false);
  const [scanRunning, setScanRunning] = useState(() =>
    loadFromStorage<string>(SCAN_KEY, teamNumber) !== null || loadFromStorage<PersistedTeamScan>(TEAM_SCAN_KEY, teamNumber) !== null
  );
  const [error, setError] = useState<string | null>(null);
  const [teamScanInfo, setTeamScanInfo] = useState<PersistedTeamScan | null>(
    () => loadFromStorage<PersistedTeamScan>(TEAM_SCAN_KEY, teamNumber)
  );

  async function handleScan(request: ScanRequest) {
    if (!token) {
      setError('Please sign in to trigger a scan');
      return;
    }
    setSubmitting(true);
    setError(null);
    setExecutionArn(null);
    setTeamScanInfo(null);
    clearStorage(SCAN_KEY, teamNumber);
    clearStorage(TEAM_SCAN_KEY, teamNumber);
    setScanRunning(true);
    try {
      const result = await apiClient.triggerScan(request, token);
      // Empty-team short-circuit — backend returns status=NO_REPOS with
      // repoCount=0 and no executionArn when SLED has no component mappings.
      // Check this BEFORE executionArn so the empty-state panel renders
      // instead of fake scan-progress.
      if (request.type === 'team' && result.repoCount === 0) {
        const scanTeamName = teamName || result.teamNumber || 'Unknown';
        const scanTeamNumber = teamNumber ?? result.teamNumber ?? '';
        setTeamScanInfo({ repoCount: 0, teamName: scanTeamName, teamNumber: scanTeamNumber, startTime: Date.now(), repoExecutions: [] });
        setScanRunning(false);
      } else if (result.executionArn) {
        setExecutionArn(result.executionArn);
        saveToStorage(SCAN_KEY, teamNumber, result.executionArn);
      } else if (request.type === 'team' && result.repoCount !== undefined && result.repoCount > 0) {
        const info: PersistedTeamScan = {
          repoCount: result.repoCount,
          teamName: teamName || result.teamNumber || 'Unknown',
          teamNumber: teamNumber ?? result.teamNumber ?? '',
          startTime: Date.now(),
          repoExecutions: result.repoExecutions ?? [],
        };
        setTeamScanInfo(info);
        saveToStorage(TEAM_SCAN_KEY, teamNumber, info);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to trigger scan');
      setScanRunning(false);
    } finally {
      setSubmitting(false);
    }
  }

  function handleComplete() {
    // Don't clear SCAN_KEY here — the user might navigate away and come back to
    // see the completed status. Storage TTL (15 min) auto-expires stale entries
    // and a new Scan Now click clears it explicitly.
    setScanRunning(false);
    onScanComplete?.();
  }

  function handleTeamScanComplete() {
    setScanRunning(false);
    onScanComplete?.();
  }

  function handleCancelScan() {
    setScanRunning(false);
    setExecutionArn(null);
    setTeamScanInfo(null);
    clearStorage(SCAN_KEY, teamNumber);
    clearStorage(TEAM_SCAN_KEY, teamNumber);
  }

  return (
    <div data-testid="delivery-scan-section">
      <div className="mb-lg">
        <ScanForm onSubmit={handleScan} disabled={submitting || scanRunning} teamNumber={teamNumber} lastScannedAt={lastScannedAt} align={align} />
        {error && <p className="badge badge-error mt-md">Error: {error}</p>}
      </div>

      {/* Team scan: no repos found */}
      {teamScanInfo && teamScanInfo.repoCount === 0 && (
        <section className="card mb-lg" data-testid="team-scan-result">
          <h3 className="text-lg font-semibold mb-md">Nothing to scan</h3>
          <p style={{ color: 'var(--genai-sem-text-secondary)' }}>
            <strong>{teamScanInfo.teamName}</strong> has no components or repositories
            mapped in SLED, so there are no repos to scan and nothing will appear on the
            dashboard for this team.
          </p>
          <p style={{ color: 'var(--genai-sem-text-secondary)', marginTop: 'var(--genai-space-sm)' }}>
            <strong>What to try:</strong>
          </p>
          <ul style={{ color: 'var(--genai-sem-text-secondary)', marginLeft: 'var(--genai-space-md)', lineHeight: 1.6 }}>
            <li>Pick a sibling team under the same Release Train that owns components in SLED.</li>
            <li>Or scan the parent Release Train to roll up all of its scrum/AIT teams.</li>
            <li>Long term: ask SLED owners to register components/workloads to this team.</li>
          </ul>
        </section>
      )}

      {/* Team scan: progress tracker */}
      {teamScanInfo && teamScanInfo.repoCount > 0 && (
        <section className="mb-lg">
          <div className="flex items-center justify-between mb-md">
            <div />
            <button
              onClick={handleCancelScan}
              className="text-xs text-muted"
              style={{ cursor: 'pointer', border: 'none', background: 'none', textDecoration: 'underline' }}
              title="Hides progress panel — background scans will continue to finish"
            >
              hide progress
            </button>
          </div>
          <TeamScanProgress
            teamNumber={teamScanInfo.teamNumber}
            teamName={teamScanInfo.teamName}
            expectedCount={teamScanInfo.repoCount}
            scanStartTime={teamScanInfo.startTime}
            repoExecutions={teamScanInfo.repoExecutions}
            onComplete={handleTeamScanComplete}
          />
        </section>
      )}

      {/* Single repo/org scan progress */}
      {executionArn && (
        <section>
          <ScanProgress executionArn={executionArn} onComplete={handleComplete} />
        </section>
      )}
    </div>
  );
}
